import {
  FormControl,
  CardContent,
  TextField,
  Link,
  Button,
  Grid,
  Stack,
  FormControlLabel,
  Checkbox,
  Box,
  Alert,
} from "@mui/material";
import Typography from "@mui/material/Typography";

import axios from "axios";
import { useState, useRef } from "react";
import { useSnackbar } from "notistack";
import Logo from "../../images/dalvado-logo.png";
import { set } from "date-fns";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { authActions } from "../../store/auth";
import { useDispatch } from "react-redux";
import { WarningAmberOutlined } from "@mui/icons-material";
import ConsoleHelper from "../../utils/ConsoleHelper";

const CodeInput = ({ codeLength, onCodeFilled, disabled = false }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [code, setCode] = useState(
    Array.from({ length: codeLength }, () => "")
  );
  const inputs = useRef([]);

  // Track last submitted value to avoid re-triggering when disabled toggles
  const lastSubmittedRef = useRef("");

  useEffect(() => {
    if (disabled) return; // do nothing while disabled
    const value = code.join("");
    if (value.length === codeLength && value !== lastSubmittedRef.current) {
      lastSubmittedRef.current = value;
      onCodeFilled(value);
    }
  }, [code, disabled, codeLength, onCodeFilled]);

  // If the user edits the code to be shorter, allow re-submission later
  useEffect(() => {
    const value = code.join("");
    if (value.length < codeLength && lastSubmittedRef.current !== "") {
      lastSubmittedRef.current = "";
    }
  }, [code, codeLength]);
  useEffect(() => {
    setCode(Array.from({ length: codeLength }, () => ""));
  }, [codeLength]);

  const focusNext = (index, value) => {
    let newCode = [...code];
    let i = index;

    // Remove special characters and spaces, convert to uppercase
    value = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    // console.log(`focusNext :${index},${value}`);
    if (value === "") {
      // Delete operation
      newCode[index] = "";
      setCode(newCode);
      if (index > 0) {
        inputs.current[index - 1].focus();
      }
      return;
    }

    for (let char of value) {
      if (i >= code.length) break;
      newCode[i] = char;
      i++;
    }

    setCode(newCode);

    if (i < code.length) {
      inputs.current[i].focus();
    }
  };

  const handlePaste = (e) => {
    if (disabled) return;
    e.preventDefault();
    // Remove special characters and spaces, convert to uppercase
    const pastedData = e.clipboardData
      .getData("text/plain")
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase();

    focusNext(0, pastedData);
  };

  const handleOnInput = (index, e) => {
    if (disabled) return;
    if (code[index] && e.target.value.length > 1 && index < code.length - 1) {
      inputs.current[index + 1].focus();
      //get the next character of e.target.value
      const nextChar = e.target.value.charAt(1);
      focusNext(index + 1, nextChar);
      return;
    }
    focusNext(index, e.target.value);
  };
  const handleKeyDown = (index, e) => {
    if (disabled) return;
    const { key } = e;
    if (key === "Backspace") {
      let newCode = [...code];

      // Empty current and move focus to the previous if it's not the first input
      if (code[index] === "" && index > 0) {
        newCode[index - 1] = "";
        setCode(newCode);
        inputs.current[index - 1].focus();
      }

      // Empty current if it's the first input
      if (code[index] === "" && index === 0) {
        newCode[index] = "";
        setCode(newCode);
      }
    }
  };

  return (
    <Grid container spacing={2} justifyContent="center" mb={2}>
      {code.map((digit, index) => (
        <Grid item key={index}>
          <TextField
            inputRef={(ref) => (inputs.current[index] = ref)}
            variant="outlined"
            value={digit}
            disabled={disabled}
            inputProps={{
              style: {
                width: "2ch",
                textAlign: "center",
                letterSpacing: "0.5ch",
              },
              maxLength: 2,
            }}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onInput={(e) => handleOnInput(index, e)}
            onPaste={handlePaste}
          />
        </Grid>
      ))}
    </Grid>
  );
};
export default CodeInput;
